import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTooltip, NgbTooltipConfig, NgbTypeaheadModule, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { DeviceEnum, FRAME_LIST, FrameIDTypeEnum, SearchItems, ToastService } from '../../core';
import { deviceListFilter, IReceivedCloneControl, noFramesAvailable, patientId_notUnique, planId_notUnique, ReceivedCloneControl, receivedCloneErrorMsgs, ReceivedCloneForm, setReceivedCloneValidators, unsupportedModel } from '../../models';
import { ReceivedService, TlhexService, UserService } from '../../services';
import { BaseComponent, FormValidationMsgDirective } from '../../shared';


/**
 * Modal component to clone received plan.
 */
@Component({
	selector: 'received-clone-modal',
	templateUrl: './received-clone-modal.component.html',
	providers: [NgbTooltipConfig],
	imports: [
		NgbTooltip,
		FormsModule,
		NgbTypeaheadModule,
		ReactiveFormsModule,
		FormValidationMsgDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ReceivedCloneModalComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

	public activeModal = inject(NgbActiveModal);
	private readonly userSrv = inject(UserService);
	private readonly tlhexSrv = inject(TlhexService);
	private readonly toastSrv = inject(ToastService);
	private readonly receivedSrv = inject(ReceivedService);
	private readonly tooltipConfig = inject(NgbTooltipConfig);

	@Input() planGuid: string = '';
	@Input() planNotes: string = '';
	@Input() frameId: FrameIDTypeEnum | undefined;
	@Input() set device(val: DeviceEnum | undefined | null) {
		this.isDeviceVisible = !val || val === DeviceEnum.ToBeDefined;
		this.isDeviceVisible && this.cloneForm.controls.device.addValidators(Validators.required);
	};

	item = signal<SearchItems | null>(null);
	private readonly hasProd = this.userSrv.hasProduct();
	deviceList = computed(() => {
		return deviceListFilter(this.item(), this.hasProd?.jps, this.hasProd?.fitbone, this.hasProd?.tlhex)
	});

	isSaving = false;
	isDeviceVisible = false;

	readonly cloneForm = new FormGroup<IReceivedCloneControl>(new ReceivedCloneControl());
	readonly errorMsgs = receivedCloneErrorMsgs;

	@ViewChild('tooltipSelect', { static: false }) private readonly tooltipSelect?: NgbTooltip;
	@ViewChild('tooltipCreate', { static: false }) private readonly tooltipCreate?: NgbTooltip;
	private _tooltipSelectSub?: Subscription;
	private _tooltipCreateSub?: Subscription;
	private readonly CLOSE_DELAY = 5000;

	readonly frameList = FRAME_LIST;
	readonly isFrameIdVisible = computed(() => !!this.frameId && this._isTlhexUser());
	private readonly _isTlhexUser = toSignal(this.userSrv.isTlhex(), { initialValue: false });
	private readonly _usedFrames = toSignal(this._updateUsedFrames(), { initialValue: [] as FrameIDTypeEnum[] });

	constructor() {
		super();
		this.setTooltipConfig();
	}


	ngOnInit(): void {
		setReceivedCloneValidators(this.cloneForm);
		this.receivedSrv.loadPatientList();
		this.cloneForm.controls.planGuid.setValue(this.planGuid);
		this.cloneForm.controls.planNotes.setValue(this.planNotes);
		this.cloneForm.controls.frameId.setValue(this.frameId ?? null);
	}

	ngAfterViewInit(): void {
		if (this.tooltipSelect) {
			this._tooltipSelectSub = this.tooltipSelect.shown.pipe(delay(this.CLOSE_DELAY)).subscribe(() => this.tooltipSelect?.close());
			this.tooltipSelect.open();
		}
		if (this.tooltipCreate) {
			this._tooltipCreateSub = this.tooltipCreate.shown.pipe(delay(this.CLOSE_DELAY)).subscribe(() => this.tooltipCreate?.close());
			this.tooltipCreate.open();
		}
	}

	/**
	* Get patient list.
	*/
	get patientList(): Observable<SearchItems[]> {
		return this.receivedSrv.patientList;
	}
	/**
	 * Check if the frame is already used by the selected patient.
	 */
	isFrameUsed(frame: FrameIDTypeEnum): boolean {
		return this._usedFrames().includes(frame);
	}

	/**
	* Display name of selected patient.
	*/
	patientFormatter = (result: SearchItems) => result.patientNumber;

	/**
	* Function that filter the patients in autocomplete form.
	*/
	filterPatients = this.receivedSrv.filterPatients;

	/**
	* Show options of autocomplete form on focus.
	*/
	showPatientList(element: any): void {
		(element?._nativeElement as HTMLInputElement).value = '';
		(element?._nativeElement as HTMLInputElement).dispatchEvent(new Event('input'));
		(element?._nativeElement as HTMLInputElement).focus();
	}

	private setTooltipConfig(): void {
		this.tooltipConfig.placement = 'left';
		this.tooltipConfig.tooltipClass = 'custom-tooltip';
		this.tooltipConfig.triggers = 'manual';
		this.tooltipConfig.autoClose = false;
	}

	/**
	 * Update existing patient.
	 */
	updatePatient(event: NgbTypeaheadSelectItemEvent<SearchItems>): void {
		this.cloneForm.controls.existingPatient.setValue(event.item);
		this.cloneForm.controls.existingPatient.markAsTouched();
		this.resetNewPatient();
	}

	/**
	 * Reset new patient data.
	*/
	private resetNewPatient(): void {
		this.cloneForm.controls.patientId.setValue(null);
		this.cloneForm.controls.patientId.markAsUntouched();
		this.cloneForm.controls.patientInitials.setValue(null);
		this.cloneForm.controls.patientInitials.markAsUntouched();
		this.cloneForm.controls.existingPatient.updateValueAndValidity();
	}

	/**
	* Reset selected patient.
	*/
	resetSelectedPatient(): void {
		this.cloneForm.controls.existingPatient.setValue(null);
		this.cloneForm.controls.existingPatient.markAsUntouched();
		this.cloneForm.controls.patientId.updateValueAndValidity();
		this.cloneForm.controls.patientInitials.updateValueAndValidity();
	}

	/**
	* Trim patient id input field.
	*/
	trimPatientId(): void {
		if (this.cloneForm.value.patientId) {
			this.cloneForm.controls.patientId.setValue(this.cloneForm.value.patientId.trim());
		}
	}

	/**
	* Trim patient intitials input field.
	*/
	trimPatientInitials(): void {
		if (this.cloneForm.value.patientInitials) {
			this.cloneForm.controls.patientInitials.setValue(this.cloneForm.value.patientInitials.trim());
		}
	}

	/**
	* Trim plan id input field.
	*/
	trimPlanId(): void {
		if (this.cloneForm.value.planId) {
			this.cloneForm.controls.planId.setValue(this.cloneForm.value.planId.trim());
		}
	}

	private _updateUsedFrames(): Observable<FrameIDTypeEnum[]> {
		return this.cloneForm.controls.existingPatient.valueChanges.pipe(
			distinctUntilChanged(),
			switchMap(patient => this.isFrameIdVisible() && !!patient ? this.tlhexSrv.getUsedFrameIDs(patient.userId, patient.patientId) : of([])),
			tap(list => this.isFrameIdVisible() && this._updateFrameId(list))
		);
	}

	private _updateFrameId(usedFrames: FrameIDTypeEnum[]): void {
		if (usedFrames.length === FRAME_LIST.length) {
			this.cloneForm.controls.frameId.setValue(null);
			this.cloneForm.controls.frameId.setErrors({ [`${noFramesAvailable}`]: null });
		} else if (usedFrames.includes(this.frameId!)) {
			const newFrame = FRAME_LIST.find(f => !usedFrames.includes(f)) ?? null;
			this.cloneForm.controls.frameId.setValue(newFrame ?? null);
		} else {
			this.cloneForm.controls.frameId.setValue(this.frameId ?? null);
		}
	}

	/**
	* Submit form and handle response messages.
	*/
	confirm(): void {
		if (this.cloneForm.valid && !this.isSaving) {
			this.isSaving = true;
			this.receivedSrv.sendCloneForm(this.cloneForm.value as ReceivedCloneForm).pipe(
				finalize(() => this.isSaving = false)
			).subscribe({
				next: () => {
					this.toastSrv.showSuccess(this.labels().RECEIVED_CLONE_MODAL_COMPONENT_SUCCESS_MESSAGE);
					this.activeModal.close();
				},
				error: err => this.handleCloneError(err)
			});
		}
	}

	private handleCloneError(err: Error): void {
		this.toastSrv.showError(this.labels().RECEIVED_CLONE_MODAL_COMPONENT_ERROR_MESSAGE);
		switch (err.message) {
			case 'PatientIdNotUnique': {
				this.cloneForm.controls.patientId.setErrors({ [`${patientId_notUnique}`]: null });
				break;
			}
			case 'PlanIdNotUnique': {
				this.cloneForm.controls.planId.setErrors({ [`${planId_notUnique}`]: null });
				break;
			}
			case 'UnsupportedModel': {
				if (this.isDeviceVisible) {
					this.cloneForm.controls.device.setErrors({ [`${unsupportedModel}`]: null });
				}
				break;
			}
		}
	}


	ngOnDestroy(): void {
		this._tooltipSelectSub?.unsubscribe();
		this._tooltipCreateSub?.unsubscribe();
	}

}
